import { default as React } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import PersonIcon from '@mui/icons-material/Person';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  ownersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
    borderRadius: theme.spacing(0.5),
    minHeight: '48px',
    border: `1px dashed ${theme.palette.mode === 'dark' ? '#424242' : '#ddd'}`,
    marginBottom: theme.spacing(1.5),
  },
  ownerChip: {
    height: '32px',
    fontSize: '0.875rem',
    fontWeight: 500,
    backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#e3f2fd',
    color: theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0',
    border: `1px solid ${
      theme.palette.mode === 'dark' ? '#616161' : '#90caf9'
    }`,
    '& .MuiChip-icon': {
      color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
    },
    '& .MuiChip-deleteIcon': {
      color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
      '&:hover': {
        color: theme.palette.mode === 'dark' ? '#64b5f6' : '#0d47a1',
      },
    },
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    width: '100%',
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface User {
  entityRef: string;
  name: string;
}

export const CodeOwnersField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string[]>) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [owners, setOwners] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Initialize owners from formData, filtering out any undefined/null values
  useEffect(() => {
    if (formData && Array.isArray(formData)) {
      const validOwners = formData.filter(
        owner => owner && typeof owner === 'string',
      );
      setOwners(validOwners);
    }
  }, [formData]);

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const entities = await catalogApi.getEntities({
          filter: { kind: 'User' },
        });
        const userList = entities.items.map(entity => ({
          entityRef: `user:default/${entity.metadata.name}`,
          name: entity.metadata.name,
        }));
        setUsers(userList);
      } catch (error) {
        // Error loading users
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [catalogApi]);

  const handleAddOwner = useCallback(
    (_event: any, newValue: User | null) => {
      if (newValue && !owners.includes(newValue.entityRef)) {
        const newOwners = [...owners, newValue.entityRef];
        setOwners(newOwners);
        onChange(newOwners);
        // Clear the input after adding
        setInputValue('');
      }
    },
    [owners, onChange],
  );

  const handleRemoveOwner = useCallback(
    (ownerToRemove: string) => {
      const newOwners = owners.filter(o => o !== ownerToRemove);
      setOwners(newOwners);
      onChange(newOwners);
    },
    [owners, onChange],
  );

  const formatOwnerName = (owner: string) => {
    if (!owner || typeof owner !== 'string') return 'Unknown';
    const parts = owner.split('/');
    return parts.length > 1 ? parts[1] : owner;
  };

  const hasErrors = rawErrors && rawErrors.length > 0;
  const availableUsers = users.filter(u => !owners.includes(u.entityRef));

  return (
    <FormControl fullWidth className={classes.root} error={hasErrors}>
      <div className={classes.header}>
        <PersonIcon color="primary" />
        <Typography className={classes.title}>
          Code Owners {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      </div>

      <Paper className={classes.ownersContainer} elevation={0}>
        {owners.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="caption">
              No code owners added yet - select users below
            </Typography>
          </div>
        ) : (
          owners.map(owner => (
            <Chip
              key={owner}
              icon={<PersonIcon />}
              label={formatOwnerName(owner)}
              onDelete={() => handleRemoveOwner(owner)}
              className={classes.ownerChip}
            />
          ))
        )}
      </Paper>

      <Autocomplete
        options={availableUsers}
        getOptionLabel={(option: User) => option.name}
        onChange={handleAddOwner}
        inputValue={inputValue}
        onInputChange={(_event: any, newInputValue: string) =>
          setInputValue(newInputValue)
        }
        value={null}
        loading={loading}
        renderInput={(params: any) => (
          <TextField
            {...params}
            label="Add Code Owner"
            variant="outlined"
            placeholder="Search for users..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {hasErrors && (
        <FormHelperText className={classes.errorText}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const CodeOwnersFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CodeOwnersField',
    component: CodeOwnersField,
  }),
);
